import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useTheme,
} from '@mui/material';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { exchangeConnectorService } from '../../services/exchangeConnector';
import { portfolioDataService } from '../../services/portfolioDataService';
import axios from 'axios';

interface BrokerStatus {
  connected: boolean;
  totalValue: number;
  positions: any[];
}

interface BrokerStatuses {
  [key: string]: BrokerStatus;
}

interface KrakenPositionExtended {
  asset: string;
  symbol: string;
  costBasis?: number;
  qty?: number;
  price?: number;
  marketValue?: number;
  assetName?: string;
  assetFullName?: string;
  unrealizedPL?: number;
  unrealizedPLPercent?: number;
}

const getAssetFullName = (symbol: string): string => {
  const assetFullNames: { [key: string]: string } = {
    'BTC': 'Bitcoin',
    'ETH': 'Ethereum',
    'XRP': 'Ripple',
    'LTC': 'Litecoin',
    'BCH': 'Bitcoin Cash',
    'ADA': 'Cardano',
    'DOT': 'Polkadot',
    'LINK': 'Chainlink',
    'XLM': 'Stellar',
    'DOGE': 'Dogecoin',
    'UNI': 'Uniswap',
    'SOL': 'Solana',
    'MATIC': 'Polygon',
    'TRX': 'TRON',
    'AVAX': 'Avalanche',
    'ATOM': 'Cosmos',
    'ALGO': 'Algorand',
    'NEAR': 'NEAR Protocol',
    'USD': 'US Dollar',
    'EUR': 'Euro',
    'GBP': 'British Pound',
    'JPY': 'Japanese Yen',
    'CAD': 'Canadian Dollar',
    'AUD': 'Australian Dollar',
    'CHF': 'Swiss Franc',
    'CNY': 'Chinese Yuan',
    'INR': 'Indian Rupee',
    'SGD': 'Singapore Dollar',
    'HKD': 'Hong Kong Dollar',
    'KRW': 'Korean Won'
  };
  return assetFullNames[symbol] || symbol;
};

const getCleanAssetName = (assetName: string) => {
  if (!assetName) return '';
  
  // Check if the asset has a futures contract suffix (.F)
  const hasFuturesSuffix = assetName.endsWith('.F');
  
  // If it has a futures suffix, temporarily remove it to clean the base asset name
  let baseAssetName = hasFuturesSuffix ? assetName.substring(0, assetName.length - 2) : assetName;
  
  // Handle special Kraken asset codes - only include the most common ones that need special handling
  const krakenSpecialCodes: { [key: string]: string } = {
    'XXBT': 'BTC',
    'XETH': 'ETH',
    'XXRP': 'XRP',
    'XXLM': 'XLM',
    'XLTC': 'LTC',
    'XXMR': 'XMR',
    'XZEC': 'ZEC',
    'ZUSD': 'USD',
    'ZEUR': 'EUR',
    'ZGBP': 'GBP',
    'ZJPY': 'JPY',
    'ZCAD': 'CAD',
    'ZAUD': 'AUD',
    'XXDG': 'DOGE',
    'XDG': 'DOGE'
  };
  
  // Clean the base asset name
  let cleanedAssetName: string;
  
  // Check for special codes first
  if (krakenSpecialCodes[baseAssetName]) {
    cleanedAssetName = krakenSpecialCodes[baseAssetName];
  }
  // Remove Kraken's X/Z prefixes if present
  else if ((baseAssetName.startsWith('X') || baseAssetName.startsWith('Z')) && baseAssetName.length > 1) {
    cleanedAssetName = baseAssetName.substring(1);
  }
  // For any other asset, just return the asset name as is
  else {
    cleanedAssetName = baseAssetName;
  }
  
  // Add back the futures suffix if it was present in the original asset name
  // This maintains transparency about the asset type
  return hasFuturesSuffix ? `${cleanedAssetName}.F` : cleanedAssetName;
};

const processPositionsWithMetrics = (positions: any[], broker: string): any[] => {
  return positions.map(position => {
    // Keep the original asset name from the API
    const originalAsset = position.asset || position.symbol || '';
    
    // Clean asset name only for display purposes
    const cleanAssetName = getCleanAssetName(originalAsset);
    
    // Get full name for the asset
    const assetFullName = getAssetFullName(cleanAssetName);
    
    // Only use the actual cost basis if provided
    const costBasis = position.costBasis;
    
    // Only calculate unrealized P/L if we have actual cost basis
    let unrealizedPL = position.unrealizedPL;
    if (costBasis && !unrealizedPL && position.marketValue) {
      unrealizedPL = position.marketValue - costBasis;
    }
    
    // Only calculate P/L percentage if we have actual cost basis
    let unrealizedPLPercent = position.unrealizedPLPercent;
    if (costBasis && unrealizedPL && !unrealizedPLPercent) {
      unrealizedPLPercent = (unrealizedPL / costBasis) * 100;
    }
    
    return {
      ...position,
      originalAsset, // Keep the original asset name
      symbol: cleanAssetName,
      assetName: cleanAssetName,
      assetFullName: assetFullName !== cleanAssetName ? assetFullName : undefined,
      costBasis,
      unrealizedPL,
      unrealizedPLPercent
    };
  });
};

export const PortfolioPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useAuth();
  const [loading, setLoading] = useState(false); // Start with false since data might be preloaded
  const [refreshing, setRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBroker, setSelectedBroker] = useState<string | null>(null);
  const [brokerStatuses, setBrokerStatuses] = useState<BrokerStatuses>({
    alpaca: { connected: false, totalValue: 0, positions: [] },
    tdameritrade: { connected: false, totalValue: 0, positions: [] },
    coinbase: { connected: false, totalValue: 0, positions: [] },
    kraken: { connected: false, totalValue: 0, positions: [] },
    binanceus: { connected: false, totalValue: 0, positions: [] },
  });

  // Define server URL for API calls
  const serverUrl = 'http://localhost:3001/api';

  // Add a state to track when data is loaded
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (state.user?.id) {
      // Check if data is already preloaded and fresh
      const preloadedData = portfolioDataService.getData();
      const isDataFresh = portfolioDataService.isDataFresh();
      
      if (isDataFresh) {
        console.log('Using preloaded portfolio data');
        // Use the preloaded data
        updateBrokerStatusesFromPreloadedData(preloadedData);
        setDataLoaded(true);
      } else {
        // If data is not fresh, fetch it
        fetchBrokerStatuses();
      }
    }
  }, [state.user]);

  // Update broker statuses from preloaded data
  const updateBrokerStatusesFromPreloadedData = (preloadedData: any) => {
    const updatedStatuses: BrokerStatuses = { ...brokerStatuses };
    
    // Update Kraken data
    if (preloadedData.kraken) {
      updatedStatuses.kraken = {
        connected: preloadedData.kraken.connected,
        totalValue: preloadedData.kraken.totalValue,
        positions: preloadedData.kraken.positions.map((position: any) => ({
          ...position,
          cleanAssetName: getCleanAssetName(position.asset),
          fullAssetName: getAssetFullName(position.asset)
        }))
      };
    }
    
    // Update Binance US data
    if (preloadedData.binanceus) {
      updatedStatuses.binanceus = {
        connected: preloadedData.binanceus.connected,
        totalValue: preloadedData.binanceus.totalValue,
        positions: preloadedData.binanceus.positions.map((position: any) => ({
          ...position,
          cleanAssetName: getCleanAssetName(position.asset),
          fullAssetName: getAssetFullName(position.asset)
        }))
      };
    }
    
    setBrokerStatuses(updatedStatuses);
  };

  const fetchBrokerStatuses = async () => {
    try {
      if (!state.user) return;

      setLoading(true);
      setRefreshing(true);
      setDataLoaded(false); // Reset data loaded state

      // Load all data from the portfolio data service
      const portfolioData = await portfolioDataService.loadAllData(state.user.id);
      
      // Update broker statuses from the loaded data
      updateBrokerStatusesFromPreloadedData(portfolioData);
      
      setDataLoaded(true);
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('Error fetching broker statuses:', error);
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>, broker: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedBroker(broker);
  };

  const handleSettingsClose = () => {
    setAnchorEl(null);
    setSelectedBroker(null);
  };

  const handleConfigureClick = () => {
    if (selectedBroker) {
      navigate(`/${selectedBroker}-config`);
      handleSettingsClose();
    }
  };

  const handleDisconnectClick = () => {
    // Disconnect logic is no longer needed since connections are managed automatically
    // Just close the menu
    handleSettingsClose();
  };

  const handleRefreshClick = () => {
    fetchBrokerStatuses();
  };

  // Calculate total portfolio value
  const totalPortfolioValue = Object.values(brokerStatuses).reduce(
    (sum, status) => sum + (status.connected ? status.totalValue : 0),
    0
  );

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Portfolio
        </Typography>
        <Tooltip title="Refresh portfolio data">
          <IconButton 
            onClick={handleRefreshClick} 
            sx={{ ml: 2 }}
            disabled={refreshing}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box mb={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Total Portfolio Value
            </Typography>
            <Typography variant="h3" color="primary">
              ${(totalPortfolioValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {Object.entries(brokerStatuses).map(([broker, status]) => {
            if (!status.connected) return null;

            const isCryptoExchange = ['coinbase', 'kraken', 'binanceus'].includes(broker);
            const BrokerIcon = isCryptoExchange ? CurrencyBitcoinIcon : AccountBalanceIcon;
            const name = {
              alpaca: 'Alpaca',
              tdameritrade: 'TD Ameritrade',
              coinbase: 'Coinbase',
              kraken: 'Kraken',
              binanceus: 'Binance.US'
            }[broker];

            return (
              <Grid item xs={12} key={broker}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <BrokerIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">{name}</Typography>
                      <Box ml="auto" display="flex" alignItems="center">
                        <Chip
                          icon={status.connected ? <CheckCircleIcon /> : <ErrorIcon />}
                          label={status.connected ? 'Connected' : 'Not Connected'}
                          color={status.connected ? 'success' : 'error'}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Tooltip title={`${name} Settings`}>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleSettingsClick(e, broker)}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Typography variant="h5" color="primary" gutterBottom>
                      ${(status.totalValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" gutterBottom>
                      Positions ({status.positions.length})
                    </Typography>
                    
                    {status.positions.length > 0 ? (
                      <TableContainer 
                        component={Paper} 
                        sx={{ 
                          boxShadow: 'none', 
                          mb: 2,
                          maxHeight: '400px', 
                          overflow: 'auto'
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Asset</TableCell>
                              <TableCell align="right">Value (USD)</TableCell>
                              <TableCell align="right">Quantity</TableCell>
                              <TableCell align="right">Price (USD)</TableCell>
                              <TableCell align="right">Cost Basis</TableCell>
                              <TableCell align="right">P/L</TableCell>
                              <TableCell align="right">P/L %</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {status.positions.map((position, index) => {
                              console.log(`Rendering position ${index}:`, position);
                              return (
                                <TableRow key={`${broker}-${position.symbol || position.asset || index}`}>
                                  <TableCell component="th" scope="row">
                                    <strong>{position.symbol || position.asset || 'Unknown Asset'}</strong>
                                    {position.originalAsset && position.originalAsset !== position.symbol && (
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        Original: {position.originalAsset}
                                      </Typography>
                                    )}
                                    {position.assetFullName && position.assetFullName !== position.symbol && (
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        {position.assetFullName}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    ${(position.marketValue || position.usdValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell align="right">
                                    {(position.qty || parseFloat(position.balance) || 0).toLocaleString(undefined, { 
                                      minimumFractionDigits: 0, 
                                      maximumFractionDigits: 8 
                                    })}
                                  </TableCell>
                                  <TableCell align="right">
                                    ${(typeof position.price === 'string' ? parseFloat(position.price) : (position.price || 0)).toLocaleString(undefined, { 
                                      minimumFractionDigits: 2, 
                                      maximumFractionDigits: 8 
                                    })}
                                  </TableCell>
                                  <TableCell align="right">
                                    {position.costBasis ? 
                                      `$${(position.costBasis || 0).toLocaleString(undefined, { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 8 
                                      })}` : 
                                      '-'
                                    }
                                  </TableCell>
                                  <TableCell align="right" sx={{ 
                                    color: (position.unrealizedPL || 0) >= 0 ? 'success.main' : 'error.main' 
                                  }}>
                                    {position.unrealizedPL !== undefined ? 
                                      `$${Math.abs(position.unrealizedPL || 0).toLocaleString(undefined, { 
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2 
                                      })}
                                      ${(position.unrealizedPL || 0) >= 0 ? ' +' : ' -'}` : 
                                      '-'
                                    }
                                  </TableCell>
                                  <TableCell align="right" sx={{ 
                                    color: (position.unrealizedPLPercent || 0) >= 0 ? 'success.main' : 'error.main',
                                    fontWeight: 'bold'
                                  }}>
                                    {position.unrealizedPLPercent !== undefined ? 
                                      `${Math.abs(position.unrealizedPLPercent || 0).toFixed(2)}%
                                      ${(position.unrealizedPLPercent || 0) >= 0 ? ' ▲' : ' ▼'}` : 
                                      '-'
                                    }
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No positions found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleSettingsClose}
        >
          <MenuItem onClick={handleConfigureClick}>Configure</MenuItem>
          <MenuItem onClick={handleDisconnectClick}>Disconnect</MenuItem>
        </Menu>
      </Box>
    </Container>
  );
};
